import moment from 'moment';

type DateSortDirection = 'most-recent' | 'least-recent';

export const compareDates = (baseDate: string | number, targetDate: string | number) => {
  return moment.utc(baseDate).isBefore(targetDate) ? -1 : 1;
};

export const compareDatesForSorting = (
  baseDate: Date | string | number,
  compareDate: Date | string | number,
  direction: DateSortDirection,
) => {
  if (direction === 'most-recent') {
    return moment(baseDate).isBefore(compareDate) ? 1 : -1;
  }
  return moment(baseDate).isBefore(compareDate) ? -1 : 1;
};

export const isValidDate = (date: Date | string | number | null | undefined): boolean => {
  return !!date && moment.utc(date).isValid();
};

export const isWeekendDate = (date: string | Date | number) => {
  const dayIndex = moment.utc(date).day();
  return dayIndex === 6 || dayIndex === 0;
};

export const minutesLeftToDate = (expirationDate: string | number) => {
  return moment.utc(expirationDate).diff(moment.utc(new Date()), 'minutes');
};

export const isFutureDate = (date: Date | string | number) => {
  return moment.utc(date).isAfter(moment.utc(new Date()));
};

export const isSameDate = (baseDate: Date | string | number, targetDate: Date | string | number, unitOfTime: 'day' | 'month') => {
  return moment.utc(baseDate).isSame(targetDate, unitOfTime);
};

export const getLeastRecentDate = <T extends Date | string | number>(baseDate: T, targetDate: T) => {
  return moment.utc(baseDate).isBefore(targetDate) ? baseDate : targetDate;
};

export const ensureNotFutureDate = (date: Date | string | number) => {
  if (isFutureDate(date)) {
    return moment.utc().toDate();
  }
  return date;
};

export const daysBetweenDates = (startDate: Date | string | number, endDate: Date | string | number) => {
  return Math.abs(moment.utc(startDate).diff(moment.utc(endDate), 'days'));
};

export const sortValueForDates = (a: string | Date | number, b: string | Date | number) => {
  return moment.utc(a).isAfter(moment.utc(b)) ? 1 : -1;
};

export const isWithinSameMinute = (baseDate: string, targetDate: string) => {
  return Math.abs(moment.utc(baseDate).diff(targetDate, 'minutes')) <= 1;
};

export const deduplicatedDates = (dates: Date[]) => {
  return dates.filter((date, index, array) => array.findIndex((val) => date == val) == index);
};

export const sortDates = (dates: Date[] | string[] | number[], direction: DateSortDirection = 'most-recent') => {
  return dates.sort((a, b) => compareDatesForSorting(a, b, direction)).map((date) => moment.utc(date).toDate());
};

export const sortDatesFunction = (
  baseDate: Date | string | number | null | undefined,
  compareDate: Date | string | number | null | undefined,
  direction: DateSortDirection = 'least-recent',
) => {
  if (baseDate) {
    return 1;
  }
  if (compareDate) {
    return -1;
  }
  if (direction === 'most-recent') {
    return moment(baseDate).isBefore(compareDate) ? 1 : -1;
  }
  return moment(baseDate).isBefore(compareDate) ? -1 : 1;
};

export const getNow = () => {
  return moment.utc().toDate();
};

export const getToday = () => {
  return moment.utc().startOf('day').toDate();
};

export const getTodayPastDays = (dayCount: number) => {
  return moment.utc().startOf('day').subtract(dayCount, 'days').toDate();
};
export const getTodayPlusDays = (dayCount: number) => {
  return moment.utc().startOf('day').add(dayCount, 'days').toDate();
};

export const findNextFridayInWeeks = (weeksLookup: number) => {
  const FridayIndex = 5;
  return moment.utc().add(weeksLookup, 'weeks').isoWeekday(FridayIndex).toDate();
};

export const daysLeftToFutureDate = (date: Date | string | number) => {
  return moment.utc(date).endOf('day').diff(moment.utc(new Date()), 'days');
};

export const daysSincePastDate = (date: Date | string | number) => {
  return moment.utc(new Date()).diff(date, 'days') || 0;
};

export const secondsSincePastDate = (date: Date | string | number) => {
  return moment.utc(new Date()).diff(date, 'seconds') || 0;
};
